import { AxiosResponse } from "axios";
import ftApiClient from "./ftApiClient";
import { normalizeItemsEnvelope } from "./normalizeItemsEnvelope";
import {
    ICustomerDetail,
    ICustomerInfo,
    ISetCustomerData,
    IQuote,
    ITripShapeUpdate,
    IAddItemToNewQuote,
    IAddItemToExistingQuote,
    IUpdateCartItemData,
    ICreateOrderData,
    ISharedPaymentLink,
    ICartItemData,
    ICustomerQuote,
    IPayNowResponseData,
} from "@interfaces";
import { IAPIResult } from "src/Interfaces/api";
import { buildQuoteRemoveUrl, RemovePayloadCart } from "@utilities";
import { emailFromToken } from "src/Utilities/customerQuoteHelpers";

interface ISendPaymentEmailsData {
    quoteId: string;
    redeemerId?: number;
    email?: string;
    name?: string;
    isNew?: boolean;
    pointsApplied?: number | null;
    commissionApplied?: number | null;
}

interface IAddPaymentReceiverData {
    quoteId: string;
    email: string;
    name: string;
    redeemerId: number | null;
}

export const quoteApi = {
    newQuote: (params: IAddItemToNewQuote) => {
        const url = "/quotes/new/v2";
        return ftApiClient.post(url, params);
    },
    getQuotes: () => {
        const url = "/quotes";
        return ftApiClient.get<IAPIResult<IQuote[]>>(url);
    },
    getAllQuotes: () => {
        const url = "/quotes/all";
        return ftApiClient.get<IAPIResult<IQuote[]>>(url);
    },
    getMyQuotes: () => {
        const url = "/quotes/my";
        return ftApiClient.get<IAPIResult<IQuote[]>>(url);
    },
    getQuoteDetails: async (
        quoteId: number | string,
    ): Promise<AxiosResponse<IAPIResult<ICartItemData[]>>> => {
        // BREAKING: `data` is now `{ trip_start_date, trip_duration, items }`
        // (was a bare array). Unwrap so callers keep reading `data.data` as the
        // items array; legacy bare-array responses pass through unchanged.
        const url = `/quotes/${quoteId}/items`;
        const res = await ftApiClient.get<IAPIResult<ICartItemData[]>>(url);
        return normalizeItemsEnvelope(res);
    },
    deleteQuote: (quoteId: number | string) => {
        const url = `quotes/${quoteId}`;
        return ftApiClient.delete(url);
    },
    addQuoteItem: (quoteId: string, itemData: IAddItemToExistingQuote) => {
        const url = `/quotes/${quoteId}/items/add/v2`;
        return ftApiClient.put(url, itemData);
    },
    updateQuoteItem: (params: IUpdateCartItemData[], quoteId: number | string) => {
        const url = `cart/items/v2?quoteId=${quoteId}`;
        return ftApiClient.put(url, params);
    },
    updateQuoteItems: (params: IUpdateCartItemData[], quoteId: number | string) => {
        const url = `cart/items?quoteId=${quoteId}`;
        return ftApiClient.put(url, params);
    },
    deleteQuoteItem: (payload: RemovePayloadCart, quoteId: string | number) => {
        const url = buildQuoteRemoveUrl(payload, quoteId);
        return ftApiClient.delete(url);
    },
    setCustomers: (quoteId: number | string, params: ICustomerInfo[]) => {
        const url = `cart/redeemers?quoteId=${quoteId}`;
        return ftApiClient.put(url, params);
    },
    addCustomers: (quoteId: number | string, params: ISetCustomerData) => {
        const url = `cart/redeemers?quoteId=${quoteId}`;
        return ftApiClient.post(url, params);
    },
    getCustomers: (quoteItemId: number | string) => {
        const url = `cart/redeemers?quoteId=${quoteItemId}`;
        return ftApiClient.get<IAPIResult<ICustomerDetail[]>>(url);
    },
    createOrder: (data: ICreateOrderData) => {
        const url = `quotes/${data.quoteId}/order`;
        return ftApiClient.post(url, data);
    },
    addPaymentReceiver: (data: IAddPaymentReceiverData) => {
        const url = `/quotes/${data.quoteId}/add/payment/receivers`;
        return ftApiClient.post(url, {
            redeemerId: data.redeemerId,
            email: data.email,
            name: data.name,
        });
    },
    getPaymentReceivers: (quoteId: string) => {
        const url = `/quotes/${quoteId}/list/payment/receivers`;
        return ftApiClient.get<IAPIResult<ICustomerDetail[]>>(url);
    },
    getSharedPaymentLinks: (quoteId: string) => {
        const url = `/quotes/${quoteId}/list/payment/shared`;
        return ftApiClient.get<IAPIResult<ISharedPaymentLink[]>>(url);
    },
    sendPaymentEmails: (data: ISendPaymentEmailsData) => {
        const url = `/quotes/${data.quoteId}/payment/share${data.isNew ? "?isNew=true" : ""}`;
        const payload = data.isNew
            ? {
                  email: data.email,
                  name: data.name,
                  pointsApplied: data.pointsApplied,
                  commissionApplied: data.commissionApplied,
              }
            : {
                  redeemerId: data.redeemerId,
                  pointsApplied: data.pointsApplied,
                  commissionApplied: data.commissionApplied,
              };
        return ftApiClient.post(url, payload);
    },
    resendPaymentLink: (data: { quoteId: string; sharePaymentId: number }) => {
        const url = `/quotes/${data.quoteId}/resend/${data.sharePaymentId}`;
        return ftApiClient.post(url, {});
    },
    ShareQuotesWithContact: (data: { quoteId: string; redeemerId: number }) => {
        const url = `/quotes/share/${data.quoteId}?isRedeemer=true`;
        return ftApiClient.post(url, { redeemerId: data.redeemerId });
    },
    /**
     * Share a quote by email. `firstName` / `lastName` are optional (max 100
     * chars server-side) and are used by TDMS to show a real recipient on the
     * quote-share preview instead of a placeholder. Blank/whitespace names are
     * omitted from the payload entirely so existing email-only calls are
     * byte-identical to the previous contract.
     */
    ShareQuotesViaEmail: (data: {
        quoteId: string;
        shareToEmail: string;
        firstName?: string;
        lastName?: string;
    }) => {
        const url = `/quotes/share/${data.quoteId}`;
        const firstName = data.firstName?.trim();
        const lastName = data.lastName?.trim();
        return ftApiClient.post(url, {
            shareToEmail: data.shareToEmail,
            ...(firstName ? { firstName } : {}),
            ...(lastName ? { lastName } : {}),
        });
    },
    getQuotesSharedToMe: () => {
        const url = "/quotes/share/to/me";
        return ftApiClient.get(url);
    },
    getQuotePreview: (quoteId: number | string) => {
        const url = `/quotes/${quoteId}`;
        return ftApiClient.get<IAPIResult<IQuote>>(url);
    },
    /**
     * Persist the trip "shape" (start date + duration) on a quote.
     *
     * Backend contract: the shape rides on the V2 add-to-quote endpoint, which
     * supports partial updates — sending only `{ tripStartDate, tripDuration }`
     * (no items) updates just those fields, and a null field is left untouched.
     * Kept as a dedicated call so a shape write can never block the core
     * item-save flow — callers invoke it best-effort.
     */
    updateTripShape: (quoteId: number | string, shape: ITripShapeUpdate) => {
        const url = `/quotes/${quoteId}/items/add/v2`;
        return ftApiClient.put(url, shape);
    },
    acceptQuoteInvite: (quoteShareId: number | string) => {
        const url = `/quotes/share/accept/${quoteShareId}`;
        return ftApiClient.post(url, {});
    },
    rejectQuoteInvite: (quoteShareId: number | string) => {
        const url = `/quotes/share/reject/${quoteShareId}`;
        return ftApiClient.post(url, {});
    },
    getSentInvitations: (quoteId: number | string) => {
        const url = `/quotes/share/${quoteId}/users`;
        return ftApiClient.get<IAPIResult<any[]>>(url);
    },
    updateCartItemsForQuote: (quoteId: string) => {
        const url = `cart/items/update?quoteId=${quoteId}`;
        return ftApiClient.put(url);
    },
    /**
     * Load a TDMS quote opened through the public customer link.
     *
     * Backend contract: the backend validates `randomString`, fetches the quote
     * from the TDMS API, and returns it in the standard `IQuote` shape (plus an
     * optional `customer_comment`). The frontend never calls TDMS directly and
     * needs no shared/agent token for this flow — it's an anonymous read.
     * Error statuses the page distinguishes: 404 = unknown quote,
     * 401/403/410 = invalid or expired link.
     */
    getCustomerQuote: (
        quoteId: number | string,
        quoteToken: string,
        randomString: string,
    ): Promise<AxiosResponse<IAPIResult<ICustomerQuote>>> => {
        // The flavor segment is already baked into ftApiClient's baseURL
        // (REACT_APP_API_URL_V2 = .../api/peterpans for peterpans, .../api for
        // freelance), so this single relative path resolves to the backend's
        // /api[/peterpans]/customerQuote/{orderId}/{token}/{hash} for both
        // flavors. `quoteToken` is the base64 `email::bookingReference` segment
        // forwarded to the backend exactly as it appears in the public link.
        //
        // The backend also requires the email (the LHS of the decoded token) as
        // an `?email=` query param to authorise the read; without it it responds
        // 403 "Please enter the email address this quote was sent to." We derive
        // it from the token so the public link works on a plain click.
        const url = `/customerQuote/${quoteId}/${quoteToken}/${randomString}`;
        const email = emailFromToken(quoteToken);
        return ftApiClient.get<IAPIResult<ICustomerQuote>>(
            url,
            email ? { params: { email } } : undefined,
        );
    },
    /**
     * Save a customer's comment back onto a quote opened via the public link.
     * Quote-level for v1. The saved text is echoed back as `customer_comment`
     * on the next `getCustomerQuote` so it persists across reloads. The
     * `randomString` is re-sent so the backend can re-validate the link on write.
     */
    saveCustomerQuoteComment: (
        quoteId: number | string,
        quoteToken: string,
        randomString: string,
        comment: string,
    ) => {
        const url = `/customerQuote/${quoteId}/${quoteToken}/${randomString}/comment`;
        const email = emailFromToken(quoteToken);
        return ftApiClient.post(
            url,
            { comment },
            email ? { params: { email } } : undefined,
        );
    },
    /**
     * Create the order for a quote opened via the public link and return the
     * hosted-payment redirect. The quote already holds every booking detail, so
     * the customer just pays the owing balance — no cart, no availability
     * re-check. Mirrors the authenticated `createOrder` (`POST /quotes/{id}/order`)
     * but authorises anonymously via the same `?email=` param as the other
     * customerQuote calls. Returns the `payNow.redirectUrl` to hard-redirect to.
     */
    createCustomerQuoteOrder: (
        quoteId: number | string,
        quoteToken: string,
        randomString: string,
    ): Promise<AxiosResponse<IAPIResult<IPayNowResponseData>>> => {
        const url = `/customerQuote/${quoteId}/${quoteToken}/${randomString}/order`;
        const email = emailFromToken(quoteToken);
        return ftApiClient.post<IAPIResult<IPayNowResponseData>>(
            url,
            { paymentType: "pay-now", pointsApplied: 0 },
            email ? { params: { email } } : undefined,
        );
    },
};
